import React from 'react';
import {Box, Button, Card, CardContent, Chip, Divider, Typography} from '@mui/material';
import {ArrowForward, Flight, FlightTakeoff, InfoOutlined} from '@mui/icons-material';
import {t} from 'ttag';
import {DateTime} from 'luxon';
import {IFlight} from '../../store/apis/interfaces/flight';
import FlightStatusBadge from './FlightStatusBadge';

const fmt = (iso: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat('HH:mm') : '—';

const ataStaMinutes = (flight: IFlight): number => {
  const sta = flight.arrival?.scheduledTime;
  const ata = flight.arrival?.actualTime ?? flight.arrival?.estimatedTime;
  if (!sta || !ata) return 0;
  return Math.max(0, Math.round(DateTime.fromISO(ata).diff(DateTime.fromISO(sta), 'minutes').minutes));
};


interface Props {
  flight: IFlight;
  searchDate: string;
  carrier: string;
  flightNumber: string;
  onContinue: () => void;
}

const FlightResultCard = ({flight, searchDate, carrier, flightNumber, onContinue}: Props) => {
  const {departure, arrival, origin, destination, status, aircraft} = flight;

  const totalDelayMins = ataStaMinutes(flight);
  const isCancelled = (status ?? '').toLowerCase().includes('cancel');

  const hasATD = !!departure.actualTime;
  const hasATA = !!arrival?.actualTime;
  const flightDateTime = departure.scheduledTime
    ? DateTime.fromISO(departure.scheduledTime)
    : DateTime.fromFormat(searchDate, 'yyyy-MM-dd');
  const isOver24h = DateTime.now().diff(flightDateTime, 'hours').hours > 24;
  const showAwaitingInfo = isOver24h && !isCancelled && !hasATD && !hasATA;

  const allDelays = [...(departure.delays ?? []), ...(arrival?.delays ?? [])].filter(
    (d, i, arr) => arr.findIndex(x => x.code === d.code) === i
  );
  const hasDelay = totalDelayMins > 0 || allDelays.length > 0;

  const formattedDate = DateTime.fromFormat(searchDate, 'yyyy-MM-dd').toLocaleString(DateTime.DATE_FULL);

  // Flight-line colour scheme
  const isWarning   = hasDelay || showAwaitingInfo;
  const lineColor   = isCancelled ? '#d32f2f' : isWarning ? '#ed6c02' : '#3f51b5';
  const lineColorHi = isCancelled ? '#ef5350' : isWarning ? '#ff9800' : '#7986cb';
  const routeColor  = isCancelled ? 'error.main' : isWarning ? 'warning.main' : 'primary.main';
  const topBorder   = isCancelled ? 'error.main' : isWarning ? 'warning.main' : 'primary.main';

  const depRows = [
    {label: 'STD', time: fmt(departure.scheduledTime)},
    {label: 'ETD', time: fmt(departure.estimatedTime)},
    {label: 'ATD', time: fmt(departure.actualTime)},
  ];
  const arrRows = [
    {label: 'STA', time: fmt(arrival?.scheduledTime ?? null)},
    {label: 'ETA', time: fmt(arrival?.estimatedTime ?? null)},
    {label: 'ATA', time: fmt(arrival?.actualTime     ?? null)},
  ];

  return (
    <Card
      elevation={3}
      sx={{mt: 3, borderRadius: 2, borderTop: '3px solid', borderColor: topBorder, overflow: 'visible'}}
    >
      <CardContent sx={{p: 3, pb: '24px !important'}}>

        {/* ── Header ── */}
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
            <Box sx={{
              background: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)',
              color: 'white',
              borderRadius: 2,
              width: 56,
              height: 56,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 3px 10px rgba(1,87,155,0.45)',
              gap: 0.25,
            }}>
              <Typography sx={{fontWeight: 900, fontSize: '1rem', letterSpacing: '0.1em', lineHeight: 1}}>
                {carrier.toUpperCase()}
              </Typography>
              <FlightTakeoff sx={{fontSize: '0.7rem', opacity: 0.75}} />
            </Box>
            <Box>
              <Typography variant='h6' fontWeight='bold' lineHeight={1.2}>
                {carrier.toUpperCase()}{flightNumber}
              </Typography>
              <Typography variant='body2' color='text.secondary'>{formattedDate}</Typography>
              {aircraft?.registration && (
                <Typography variant='caption' color='text.secondary'>
                  {aircraft.type} · {aircraft.registration}
                </Typography>
              )}
            </Box>
          </Box>
          <FlightStatusBadge status={status} hasDelay={hasDelay} />
        </Box>

        {/* ── Route ── */}
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          px: 2.5,
          py: 2,
          bgcolor: 'grey.50',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          mb: ((hasDelay && allDelays.length > 0) || showAwaitingInfo) ? 2 : 0,
        }}>

          {/* Origin + departure times */}
          <Box sx={{minWidth: 80}}>
            <Typography sx={{fontSize: '2rem', fontWeight: 800, fontFamily: 'monospace', lineHeight: 1, color: 'text.primary'}}>
              {origin}
            </Typography>
            <Box sx={{mt: 1, display: 'flex', flexDirection: 'column', gap: 0.4}}>
              {depRows.map(({label, time}) => (
                <Box key={label} sx={{display: 'flex', alignItems: 'baseline', gap: 0.75}}>
                  <Typography sx={{fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', color: 'text.secondary', width: 28, lineHeight: 1.5}}>
                    {label}
                  </Typography>
                  <Typography sx={{fontSize: '0.95rem', fontWeight: 200, fontFamily: 'monospace', color: time !== '—' ? 'text.primary' : 'rgba(0,0,0,0.28)', lineHeight: 1.4}}>
                    {time}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Flight line + chips */}
          <Box sx={{flex: 1, pt: '1rem'}}>
            <Box sx={{display: 'flex', alignItems: 'center'}}>
              <Box sx={{width: 9, height: 9, borderRadius: '50%', border: '2.5px solid', borderColor: lineColor, bgcolor: 'grey.50', flexShrink: 0}} />
              <Box sx={{
                flex: 1, height: 4,
                backgroundImage: `radial-gradient(circle, ${lineColor} 1.5px, transparent 1.5px)`,
                backgroundSize: '12px 4px',
                backgroundRepeat: 'repeat-x',
                backgroundPosition: 'center',
                opacity: 0.55,
              }} />
              <Flight sx={{color: routeColor, mx: 0.5, fontSize: '1.5rem'}} />
              <Box sx={{
                flex: 1, height: 4,
                backgroundImage: `radial-gradient(circle, ${lineColor} 1.5px, transparent 1.5px)`,
                backgroundSize: '12px 4px',
                backgroundRepeat: 'repeat-x',
                backgroundPosition: 'center',
                opacity: 0.55,
              }} />
              <Box sx={{width: 9, height: 9, borderRadius: '50%', border: '2.5px solid', borderColor: lineColor, bgcolor: 'grey.50', flexShrink: 0}} />
            </Box>
            {(hasDelay || isCancelled) && (
              <Box sx={{display: 'flex', justifyContent: 'center', mt: 0.75, gap: 0.75}}>
                {hasDelay && !isCancelled && (
                  <Chip label={`+${totalDelayMins} min`} size='small' color='warning' variant='filled' sx={{fontSize: '0.65rem', fontWeight: 700, height: 20}} />
                )}
                {isCancelled && (
                  <Chip label={t`Cancelled`} size='small' color='error' variant='filled' sx={{fontSize: '0.65rem', fontWeight: 700, height: 20}} />
                )}
              </Box>
            )}
          </Box>

          {/* Destination + arrival times */}
          <Box sx={{minWidth: 80, display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
            <Typography sx={{fontSize: '2rem', fontWeight: 800, fontFamily: 'monospace', lineHeight: 1, color: 'text.primary'}}>
              {destination}
            </Typography>
            <Box sx={{mt: 1, display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'flex-end'}}>
              {arrRows.map(({label, time}) => (
                <Box key={label} sx={{display: 'flex', alignItems: 'baseline', gap: 0.75}}>
                  <Typography sx={{fontSize: '0.95rem', fontWeight: 200, fontFamily: 'monospace', color: time !== '—' ? 'text.primary' : 'rgba(0,0,0,0.28)', lineHeight: 1.4}}>
                    {time}
                  </Typography>
                  <Typography sx={{fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', color: 'text.secondary', width: 28, textAlign: 'right', lineHeight: 1.5}}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

        </Box>

        {/* ── Awaiting info disclaimer ── */}
        {showAwaitingInfo && (
          <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1.25,
            mt: 2, px: 2, py: 1.5,
            bgcolor: 'rgba(237,108,2,0.07)',
            border: '1px solid', borderColor: 'rgba(237,108,2,0.3)',
            borderRadius: 2,
          }}>
            <InfoOutlined sx={{color: 'warning.main', fontSize: '1.1rem', mt: '1px', flexShrink: 0}} />
            <Box>
              <Typography variant='body2' sx={{fontWeight: 700, color: 'warning.dark', lineHeight: 1.4}}>
                {t`Awaiting flight information`}
              </Typography>
              <Typography variant='caption' sx={{color: 'text.secondary', lineHeight: 1.5}}>
                {t`Consolidated data for this flight is not yet available. The actual times may be updated once the information is received.`}
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── No ATD disclaimer ── */}
        {!hasATD && !isCancelled && !showAwaitingInfo && (
          <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1.25,
            mt: 2, px: 2, py: 1.5,
            bgcolor: 'rgba(237,108,2,0.07)',
            border: '1px solid', borderColor: 'rgba(237,108,2,0.3)',
            borderRadius: 2,
          }}>
            <InfoOutlined sx={{color: 'warning.main', fontSize: '1.1rem', mt: '1px', flexShrink: 0}} />
            <Box>
              <Typography variant='body2' sx={{fontWeight: 700, color: 'warning.dark', lineHeight: 1.4}}>
                {t`Boarding Declaration not available`}
              </Typography>
              <Typography variant='caption' sx={{color: 'text.secondary', lineHeight: 1.5}}>
                {t`This flight has no confirmed actual departure time (ATD). A Boarding Declaration can only be issued once the ATD has been recorded.`}
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── Delay codes ── */}
        {hasDelay && allDelays.length > 0 && (
          <Box sx={{
            mt: 2,
            borderLeft: '3px solid',
            borderColor: 'warning.main',
            pl: 2, py: 1,
            bgcolor: 'rgba(237,108,2,0.05)',
            borderRadius: '0 8px 8px 0',
          }}>
            <Typography variant='caption' sx={{
              fontWeight: 700, letterSpacing: '0.08em',
              color: 'warning.dark', display: 'block', mb: 0.5,
            }}>
              {allDelays.length > 1 ? t`DELAY REASONS` : t`DELAY REASON`}
            </Typography>
            {allDelays.map((delay) => (
              <Box key={delay.code} sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                <Typography variant='body2' sx={{lineHeight: 1.8}}>
                  <Box component='span' sx={{color: 'warning.dark', fontWeight: 700}}>{delay.code}</Box>
                  {' – '}{delay.description}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

      </CardContent>

      {/* ── Footer ── */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        px: 3,
        py: 1.75,
        bgcolor: 'grey.50',
        borderTop: '1px solid',
        borderColor: 'divider',
        borderBottomLeftRadius: 'inherit',
        borderBottomRightRadius: 'inherit',
      }}>
        <Button
          variant='contained'
          color={isCancelled ? 'error' : isWarning ? 'warning' : 'primary'}
          endIcon={<ArrowForward />}
          onClick={onContinue}
          sx={{fontWeight: 600}}
        >
          {t`Continue with this Flight`}
        </Button>
      </Box>
    </Card>
  );
};

export default FlightResultCard;
